/**
 * CustomerContacts (CONTACT-01) — named, ROLE-labeled contacts in the 360
 * location card. "You have our nanny's number, right?" → a labeled row, not
 * four bare numbers read aloud. Scope per CUST-12: a contact is either for
 * THIS property (tenant, on-site maintenance) or for the WHOLE customer
 * (spouse, AP office) — the add form asks explicitly.
 *
 * Sources: manual today; ESC's location phone labels arrive via the
 * MIR-LOCLBL mirror, note-mentions via MIG-NOTES-01 — same table, marked.
 */
import { useEffect, useState } from "react";
import { Phone, Mail, Pencil, Plus, Trash2, UserRound } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Copyable } from "@/components/Copyable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { useEntryFormKeys } from "@/lib/keys";
import { supabase } from "@/lib/supabase";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useCustomerConsent, useSetConsent, PURPOSES, type ConsentPurpose } from "@/features/comms/consent";

export interface ContactRow {
  id: string;
  custno: string;
  locno: string | null;
  name: string;
  role: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  source: string;
  /** Correspondence flags (DEC-15 landing on the contact): who gets WHAT.
   *  Tenant gets job updates but never invoices; AP email is invoices-only;
   *  Kevin wants all. The comms engine resolves recipients from these. */
  gets_scheduling: boolean;
  gets_billing: boolean;
  gets_marketing: boolean;
  /** CONTACT-ONE-TABLE (34i97): every Pegasus-owned line lives here. */
  label?: string | null;            // Cell / Home / Work / Spouse…
  replaces?: string | null;         // the ESC line this row replaces: 'phone:<10 digits>' | 'email:<lower>'
  hidden_at?: string | null;        // switched off — on file, not shown, not dialed
  hidden_by?: string | null;
  may_authorize?: boolean;
  /** REACH-DEFAULTS-01: who gets what when a visit is booked. */
  reach_confirm?: boolean;
  reach_headsup?: "call" | "text" | null;
  reach_email?: boolean;
  sort_order?: number;
  created_at?: string | null;
}

/** "· gets updates + invoices" — the at-a-glance answer to "who do we send this to?" */
function receivesSummary(c: ContactRow): string {
  const parts = [
    c.gets_scheduling && "updates",
    c.gets_billing && "invoices",
    c.gets_marketing && "marketing",
  ].filter(Boolean);
  return parts.length ? `gets ${parts.join(" + ")}` : "no messages";
}

const COMMON_ROLES = [
  "Tenant",
  "Property manager",
  "Maintenance",
  "AP / Billing",
  "Spouse",
  "Nanny",
  "Caretaker",
  "Neighbor",
  "Office",
];

/** Customer-level contacts + the selected location's — one labeled list. */
export function useContacts(custno: string, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["contacts", custno, locno ?? "all"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ContactRow[]> => {
      let q = supabase.from("contact").select("*").eq("custno", custno);
      q = locno ? q.or(`locno.is.null,locno.eq.${locno}`) : q;
      const { data, error } = await q.order("created_at");
      if (error) throw error;
      return (data ?? []) as ContactRow[];
    },
  });
}

function useSaveContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      v: Partial<ContactRow> & { custno: string; name: string },
    ) => {
      const { data: userData } = await supabase.auth.getUser();
      if (v.id) {
        const { id, ...patch } = v;
        const { error } = await supabase
          .from("contact")
          .update({ ...patch, updated_at: new Date().toISOString() })
          .eq("id", id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("contact").insert({
          ...v,
          created_by: userData?.user?.email ?? null,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }),
  });
}

function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("contact").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }),
  });
}

export function ContactsBlock({
  custno,
  locno,
  onEmailClick,
}: {
  custno: string;
  locno: string | null | undefined;
  /** When set, clicking an email opens Pegasus's composer (passed down from
   *  the header) instead of mailto/Outlook — Seth 2026-06-12. */
  onEmailClick?: (email: string) => void;
}) {
  const contacts = useContacts(custno, locno).data ?? [];
  const canEdit = useCan("contacts.edit");
  const del = useDeleteContact();
  const [editing, setEditing] = useState<ContactRow | "new" | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);

  return (
    <div>
      {contacts.length > 0 && (
        <ul className="mb-1.5 space-y-1">
          {contacts.map((c) => (
            <li key={c.id} className="group flex items-start gap-1.5 text-sm">
              <UserRound className="mt-1 h-3 w-3 shrink-0 text-slate-300" />
              <div className="min-w-0 flex-1 leading-snug">
                <span className="font-medium text-slate-800">{c.name}</span>
                {c.role && (
                  <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 align-middle text-[11px] font-semibold text-slate-500">
                    {c.role}
                  </span>
                )}
                {!c.locno && (
                  <span
                    className="ml-1 align-middle text-[11px] text-slate-400"
                    title="Travels with the customer — shows at every property"
                  >
                    · all properties
                  </span>
                )}
                <span
                  className={cn(
                    "ml-1 align-middle text-[11px]",
                    receivesSummary(c) === "no messages"
                      ? "text-amber-600"
                      : "text-slate-400",
                  )}
                  title="Which correspondence this contact receives — edit to change"
                >
                  · {receivesSummary(c)}
                </span>
                <span className="block text-xs text-slate-600">
                  {c.phone && (
                    <Copyable text={c.phone} label="Copy phone">
                      <a
                        href={`tel:${c.phone.replace(/[^0-9+]/g, "")}`}
                        className="whitespace-nowrap font-medium text-brand-700 transition-colors hover:text-brand-900"
                      >
                        {c.phone}
                      </a>
                    </Copyable>
                  )}
                  {c.phone && c.email && " · "}
                  {c.email &&
                    (onEmailClick ? (
                      <Copyable text={c.email} label="Copy email">
                        <button
                          type="button"
                          onClick={() => onEmailClick(c.email!)}
                          title={`Email ${c.email} from Pegasus`}
                          className="transition-colors hover:text-brand-700"
                        >
                          {c.email}
                        </button>
                      </Copyable>
                    ) : (
                      <Copyable text={c.email} label="Copy email">
                        <a
                          href={`mailto:${c.email}`}
                          className="transition-colors hover:text-brand-700"
                        >
                          {c.email}
                        </a>
                      </Copyable>
                    ))}
                </span>
              </div>
              {canEdit && (
                <span className="flex shrink-0 items-center gap-0.5 opacity-40 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => setEditing(c)}
                    aria-label={`Edit ${c.name}`}
                    className="rounded p-0.5 text-slate-300 transition-colors hover:text-slate-600"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirmDel === c.id) {
                        del.mutate(c.id);
                        setConfirmDel(null);
                      } else setConfirmDel(c.id);
                    }}
                    aria-label={`Remove ${c.name}`}
                    title={confirmDel === c.id ? "Click again to remove" : "Remove"}
                    className={cn(
                      "rounded p-0.5 transition-colors",
                      confirmDel === c.id
                        ? "bg-red-50 text-red-600"
                        : "text-slate-300 hover:text-red-500",
                    )}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-700 transition-colors hover:text-brand-900"
        >
          <Plus className="h-3 w-3" />
          Add contact
        </button>
      )}
      {editing && (
        <ContactModal
          custno={custno}
          locno={locno ?? null}
          contact={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

export function ContactModal({
  custno,
  locno,
  contact,
  prefill,
  onClose,
}: {
  custno: string;
  locno: string | null;
  contact: ContactRow | null;
  /** Promote an ESC contact-grid line into an editable Pegasus contact:
   *  opens the modal pre-filled (MIR-LOCLBL "all editable from Pegasus"). */
  prefill?: { name?: string; role?: string; phone?: string; email?: string };
  onClose: () => void;
}) {
  const save = useSaveContact();
  const [name, setName] = useState(contact?.name ?? prefill?.name ?? "");
  const [role, setRole] = useState(contact?.role ?? prefill?.role ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? prefill?.phone ?? "");
  const [email, setEmail] = useState(contact?.email ?? prefill?.email ?? "");
  const [notes, setNotes] = useState(contact?.notes ?? "");
  // Scope (CUST-12): this property vs the whole customer.
  const [scope, setScope] = useState<"location" | "customer">(
    contact ? (contact.locno ? "location" : "customer") : "location",
  );
  // Correspondence flags — a new contact gets EVERYTHING by default (Seth
  // 2026-09-02: "unless we have proof in ESC that some customers have
  // unsubscribed, shouldn't everything still be checked on by default?").
  // The proof lives elsewhere and always wins: a number that texted STOP or
  // an address that unsubscribed is hard-blocked in every composer
  // (comm_endpoint.opted_out), whatever these boxes say.
  const [getsScheduling, setGetsScheduling] = useState(
    contact?.gets_scheduling ?? true,
  );
  const [getsBilling, setGetsBilling] = useState(contact?.gets_billing ?? true);
  const [getsMarketing, setGetsMarketing] = useState(
    contact?.gets_marketing ?? true,
  );
  // CONSENT-01: the boxes reflect — and write — the one consent model. A
  // purpose blocked on this person's phone or email (STOP, unsubscribe link,
  // ESC Disable Emails) shows unchecked; saving writes each box back as an
  // office decision for both lines (a texted STOP cannot be overridden).
  const consent = useCustomerConsent(custno, [
    ...(phone.trim() ? [{ channel: "sms" as const, endpoint: phone }] : []),
    ...(email.trim() ? [{ channel: "email" as const, endpoint: email }] : []),
  ]);
  const setConsent = useSetConsent();
  const blockedFor = (purpose: ConsentPurpose) =>
    (!!phone.trim() && consent.blocked("sms", phone, purpose)) || (!!email.trim() && consent.blocked("email", email, purpose));
  useEffect(() => {
    if (!consent.isSuccess) return;
    if (blockedFor("job_updates")) setGetsScheduling(false);
    if (blockedFor("billing")) setGetsBilling(false);
    if (blockedFor("marketing")) setGetsMarketing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent.isSuccess]);
  const writeConsent = () => {
    const flags: Record<ConsentPurpose, boolean> = { job_updates: getsScheduling, billing: getsBilling, marketing: getsMarketing };
    for (const p of PURPOSES) {
      const status = flags[p.key] ? "allowed" : "unsubscribed";
      if (phone.trim()) setConsent.mutate({ custno, channel: "sms", endpoint: phone, purpose: p.key, status, source: "office" });
      if (email.trim()) setConsent.mutate({ custno, channel: "email", endpoint: email, purpose: p.key, status, source: "office" });
    }
  };

  // Esc / outside-click / focus trap come from the shared Radix Modal.

  const onFormKeys = useEntryFormKeys(() => { if (name.trim() && !save.isPending) submit(); });
  const submit = () =>
    save.mutate(
      {
        id: contact?.id,
        custno,
        locno: scope === "location" ? locno : null,
        name: name.trim(),
        role: role.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
        gets_scheduling: getsScheduling,
        gets_billing: getsBilling,
        gets_marketing: getsMarketing,
      },
      { onSuccess: () => { writeConsent(); onClose(); } },
    );

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={contact ? `Edit ${contact.name}` : "Add a contact"}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!name.trim() || save.isPending}
            className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            {save.isPending
              ? "Saving…"
              : contact
                ? "Save changes"
                : "Add contact"}
          </button>
        </div>
      }
    >
      <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Name
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-autofocus=""
                placeholder="Caleb"
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Role
              </span>
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                list="contact-roles"
                placeholder="Maintenance"
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
              <datalist id="contact-roles">
                {COMMON_ROLES.map((r) => (
                  <option key={r} value={r} />
                ))}
              </datalist>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <Phone className="h-3 w-3" /> Phone
              </span>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-600">
              <span className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <Mail className="h-3 w-3" /> Email
              </span>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
              />
            </label>
          </div>
          {/* Scope — plain words, per CUST-12's explicit-at-creation rule. */}
          <div className="text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Applies to
            </span>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setScope("location")}
                disabled={!locno}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40",
                  scope === "location"
                    ? "border-brand-300 bg-brand-50 text-brand-800"
                    : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                This property
              </button>
              <button
                type="button"
                onClick={() => setScope("customer")}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                  scope === "customer"
                    ? "border-brand-300 bg-brand-50 text-brand-800"
                    : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                Whole customer (all properties)
              </button>
            </div>
          </div>
          {/* Correspondence (DEC-15): tenant gets updates but never invoices,
              AP email is invoices-only, some want everything. */}
          <div className="text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Receives
            </span>
            <div className="space-y-1.5">
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={getsScheduling}
                  onChange={(e) => setGetsScheduling(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-brand-700"
                />
                <span>
                  <span className="font-medium text-slate-700">Job updates</span>
                  <span className="block text-[11px] text-slate-400">
                    appointment confirmations, on-the-way texts, reschedules
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={getsBilling}
                  onChange={(e) => setGetsBilling(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-brand-700"
                />
                <span>
                  <span className="font-medium text-slate-700">
                    Invoices &amp; receipts
                  </span>
                  <span className="block text-[11px] text-slate-400">
                    billing only — e.g. the AP office, not the tenant
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={getsMarketing}
                  onChange={(e) => setGetsMarketing(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 accent-brand-700"
                />
                <span>
                  <span className="font-medium text-slate-700">Marketing</span>
                  <span className="block text-[11px] text-slate-400">
                    tune-up reminders, seasonal offers
                  </span>
                </span>
              </label>
            </div>
          </div>
          <label className="block text-xs text-slate-600">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Notes
            </span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="call before arriving (optional)"
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm"
            />
          </label>
        </div>
    </Modal>
  );
}
